import _ from 'lodash';
import { Exception } from '@src/types';
import { CrossFrameUtils } from '@src/utils';

interface IAlertOptionsType {
    message: string;
    isYesNoAlert: boolean;
    yesCallback: () => void;
    noCallback: () => void;
    yesText?: string;
    noText?: string;
    okayText?: string;
}

/**
 * Sets the active alert for atAlertDirective to pick up and display as a modal.
 * Only one active alert message is shown at a time; a new alert will override the currently open one.
 * Alert accepts an Exception or string and returns a promise that :
 * resolves when the modal is acknowledged or closed and
 * rejects when another alert is opened on top of the old one.
 *
 * Usage:
 *   let atAlert = new AtAlertService()
 *   atAlert.alert("Alert message") OR atAlert.alert(new Exception({message : "Error message"}))
 */
export class AtAlertService {
    private static clearedAlert = {
        message: '',
        isYesNoAlert: false,
        yesCallback: () => {
            return;
        },
        noCallback: () => {
            return;
        },
    };
    private static alertOptions: IAlertOptionsType = _.cloneDeep(AtAlertService.clearedAlert);

    private static setCallbackListeners() {
        CrossFrameUtils.registerListener('alertYesCallback', async () => {
            if (AtAlertService.alertOptions.yesCallback) {
                AtAlertService.alertOptions.yesCallback();
            }
            return Promise.resolve();
        });
        CrossFrameUtils.registerListener('alertNoCallback', async () => {
            if (AtAlertService.alertOptions.noCallback) {
                AtAlertService.alertOptions.noCallback();
            }
            return Promise.resolve();
        });
    }

    private static triggerAlert(alert: Exception | string) {
        // Another alert currently open - open the new one
        if (AtAlertService.alertOptions.message.length > 0) {
            console.info('Closing old alert, new alert set in modal');
        }

        console.warn('Triggering alert');
        if (alert instanceof Exception) {
            AtAlertService.alertOptions.message = alert.getMessage();
            console.error(alert.toString());
        } else if (_.isString(alert)) {
            AtAlertService.alertOptions.message = alert;
            console.info(alert);
        }
        const payload = {
            message: AtAlertService.alertOptions.message,
            isYesNoAlert: AtAlertService.alertOptions.isYesNoAlert,
            yesCallback: !!AtAlertService.alertOptions.yesCallback,
            noCallback: !!AtAlertService.alertOptions.noCallback,
        };
        return CrossFrameUtils.sendMessageToOtherFrame({
            frame: window,
            message: {
                method: 'setAlert',
                data: payload,
            },
        });
    }

    public static alert(alert: Exception | string, okayText?: string) {
        AtAlertService.alertOptions.isYesNoAlert = false;
        AtAlertService.alertOptions.yesCallback = null;
        AtAlertService.alertOptions.noCallback = null;
        if (okayText !== undefined) {
            AtAlertService.alertOptions.okayText = okayText;
        }
        return AtAlertService.triggerAlert(alert);
    }

    public static alertYesNo(alert: Exception | string, yesCallback: () => any, noCallback: () => any, yesText?: string, noText?: string) {
        AtAlertService.alertOptions.isYesNoAlert = true;
        AtAlertService.alertOptions.yesCallback = yesCallback;
        AtAlertService.alertOptions.noCallback = noCallback;
        if (yesText !== undefined) {
            AtAlertService.alertOptions.yesText = yesText;
        }
        if (noText !== undefined) {
            AtAlertService.alertOptions.noText = noText;
        }
        AtAlertService.setCallbackListeners();
        return AtAlertService.triggerAlert(alert);
    }

    public static resetAlert() {
        AtAlertService.alertOptions = _.cloneDeep(AtAlertService.clearedAlert);
        return CrossFrameUtils.sendMessageToOtherFrame({
            frame: window,
            message: {
                method: 'resetAlert',
            },
        });
    }

    public static getAlertMessage() {
        return AtAlertService.alertOptions.message;
    }

    public static getAlertOptions() {
        return AtAlertService.alertOptions;
    }
}
